<template>
  <div
    class="auth-form-container w-full max-w-[400px] mx-auto"
    :aria-label="STRINGS.aria.labels.forgotPasswordForm"
  >
    <!-- Request Form -->
    <template v-if="!submitted">
      <h2
        class="auth-heading font-display text-2xl font-extrabold text-arena-text-bright tracking-[0.06em] text-center mb-6"
      >
        {{ STRINGS.auth.passwordReset.heading }}
      </h2>

      <form
        class="auth-card flex flex-col gap-4 p-6 rounded-xl border border-arena-border bg-arena-surface/80 backdrop-blur-sm"
        @submit.prevent="handleSubmit"
        autocomplete="on"
        novalidate
      >
        <p class="text-sm text-arena-text-muted text-center m-0">
          {{ STRINGS.auth.passwordReset.subheading }}
        </p>

        <!-- Email -->
        <div class="flex flex-col gap-1.5">
          <label for="forgot-email" class="text-sm font-semibold text-arena-text-bright">
            {{ STRINGS.auth.labels.email }}
          </label>
          <div class="relative">
            <InputText
              id="forgot-email"
              ref="emailInput"
              v-model="email"
              type="email"
              name="email"
              class="auth-input min-h-12 touch-manipulation w-full"
              :placeholder="STRINGS.auth.placeholders.email"
              autocomplete="username"
              :aria-invalid="!!displayError"
              data-testid="forgot-email-input"
              @keyup.enter="handleSubmit"
            />
          </div>
        </div>

        <!-- Error display -->
        <p
          v-if="displayError"
          ref="errorRef"
          class="text-sm text-error-500 text-center m-0"
          role="alert"
          aria-live="polite"
          tabindex="-1"
        >
          {{ displayError }}
        </p>

        <!-- Submit button -->
        <PrimeButton
          :disabled="!canSubmit"
          data-testid="forgot-submit-button"
          class="min-h-12 touch-manipulation mt-2"
          :full-width="true"
          @click="handleSubmit"
        >
          <template v-if="passwordReset.isLoading.value">
            {{ STRINGS.auth.buttons.sendingResetLink }}<LoadingDots />
          </template>
          <template v-else>
            {{ STRINGS.auth.buttons.sendResetLink }}
          </template>
        </PrimeButton>
      </form>

      <!-- Footer links -->
      <div class="auth-footer flex flex-col items-center gap-2 mt-6 text-sm text-arena-text-muted">
        <button
          class="text-arena-text-muted hover:text-arena-text-bright bg-transparent border-none cursor-pointer text-sm p-0"
          @click="emit('navigate', 'login')"
          data-testid="forgot-back-to-login-link"
        >
          {{ STRINGS.auth.links.backToLogin }}
        </button>
      </div>
    </template>

    <!-- Success State -->
    <template v-else>
      <div
        class="success-panel flex flex-col items-center gap-6 bg-arena-surface/40 rounded-2xl p-8"
      >
        <div class="success-icon">
          <i
            class="pi pi-envelope text-5xl text-energy-primary envelope-pulse"
            aria-hidden="true"
          ></i>
        </div>

        <h2
          class="font-display text-2xl font-extrabold text-arena-text-bright tracking-[0.06em] text-center m-0"
        >
          {{ STRINGS.auth.passwordReset.successHeading }}
        </h2>

        <p class="font-mono text-energy-primary text-center m-0" data-testid="forgot-success-email">
          {{ submittedEmail }}
        </p>

        <p class="text-sm text-arena-text-muted text-center m-0">
          {{ STRINGS.auth.passwordReset.successExpiry }}
        </p>

        <button
          class="text-arena-text-muted hover:text-arena-text-bright bg-transparent border-none cursor-pointer text-sm p-0 mt-2"
          @click="emit('navigate', 'login')"
          data-testid="forgot-success-back-to-login"
        >
          {{ STRINGS.auth.links.backToLogin }}
        </button>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, nextTick } from 'vue';
import InputText from 'primevue/inputtext';
import PrimeButton from './ui/PrimeButton.vue';
import LoadingDots from './ui/LoadingDots.vue';
import { usePasswordReset } from '@/composables/usePasswordReset';
import { isAuthError } from '@/types/auth';
import STRINGS from '@/constants/strings';
import { reportClientError, ErrorLocation } from '@/telemetry/errors';

const emit = defineEmits<{
  navigate: [target: 'login' | 'homepage'];
}>();

const passwordReset = usePasswordReset();

const email = ref('');
const submitted = ref(false);
const submittedEmail = ref('');
const emailInput = ref();
const errorRef = ref<HTMLElement>();

const displayError = computed(() => {
  if (passwordReset.rateLimitCountdown.value > 0) {
    return STRINGS.templates.rateLimitCountdown(passwordReset.rateLimitCountdown.value);
  }
  if (passwordReset.error.value) {
    return passwordReset.error.value.message;
  }
  return '';
});

const canSubmit = computed(() => {
  return (
    email.value.trim().length > 0 &&
    !passwordReset.isLoading.value &&
    passwordReset.rateLimitCountdown.value === 0
  );
});

async function handleSubmit(): Promise<void> {
  if (!canSubmit.value) return;

  passwordReset.clearError();

  try {
    await passwordReset.requestReset(email.value.trim());
    submittedEmail.value = email.value.trim();
    submitted.value = true;
  } catch (err: unknown) {
    if (!isAuthError(err)) {
      reportClientError(err, ErrorLocation.AuthForgotPasswordUnexpected);
    }
    focusError();
  }
}

function focusError(): void {
  nextTick(() => {
    errorRef.value?.focus();
  });
}

onMounted(() => {
  nextTick(() => {
    const el = emailInput.value?.$el ?? emailInput.value;
    const input = el?.querySelector?.('input') ?? el;
    if (input && typeof input.focus === 'function') {
      input.focus();
    }
  });
});
</script>

<style scoped>
.auth-heading {
  animation: homepageEntrance 0.8s ease-out both;
}

.auth-card {
  animation: homepageEntrance 0.8s ease-out 0.2s both;
}

.auth-footer {
  animation: homepageEntrance 0.8s ease-out 0.3s both;
}

.success-panel {
  animation: successEntrance 1s ease-out both;
}

.success-icon {
  animation: successEntrance 1s ease-out 0.2s both;
}

@keyframes homepageEntrance {
  from {
    transform: translateY(16px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

@keyframes successEntrance {
  from {
    transform: translateY(24px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

.envelope-pulse {
  animation: envelopePulse 2s ease-in-out infinite;
}

@keyframes envelopePulse {
  0%,
  100% {
    opacity: 0.6;
  }
  50% {
    opacity: 1;
  }
}

.auth-input {
  padding: 0.75rem 1rem;
  border: 2px solid var(--color-arena-border);
  border-radius: var(--radius-input, 0.5rem);
  font-size: 1rem;
  transition: none;
  outline: none;
  background: rgba(26, 22, 18, 0.8);
  color: var(--color-arena-text-bright);
}

.auth-input:focus {
  outline: none;
}

.auth-input::placeholder {
  color: var(--color-arena-text-muted);
}

@media (prefers-reduced-motion: reduce) {
  .auth-heading,
  .auth-card,
  .auth-footer,
  .success-panel,
  .success-icon,
  .envelope-pulse {
    animation: none !important;
  }
}
</style>
